































































































































































// Basic
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  Button,
  Card,
  Checkbox,
  Form,
  FormItem,
  Option,
  Popconfirm,
  Select
} from 'element-ui';
import Pagination from '@/mixins/pagination';

// Graphql/Apollo related
import gql from 'graphql-tag';
import { billService } from '@/services/BillService';
import BillableItems from '@/graphql/queries/shipment/BillableItems.gql';

import { languageOptions } from '@/constants/languages';

import {
  AvailableLocalesEnum,
  BillItemFields
} from '@/types/graphql/schemaTypes';

import { CheckboxStatus } from '@/types/types';

interface CheckableBillItem extends BillItemFields, CheckboxStatus {}

interface CheckableShipment {
  billItems: CheckableBillItem[];
  checked?: boolean;
  id: string;
  indeterminate?: boolean;
  reference: string;
}

@Component({
  apollo: {
    shipments: {
      query: BillableItems,
      variables() {
        return {
          billArgs: { editBill: !!this.billId, unbilled: !this.billId },
          shipmentArgs: { shipmentIds: this.shipmentIds }
        };
      },
      skip() {
        return !this.shipmentIds.length;
      },
      update(data) {
        const processedData = data.shipments.map(
          (shipment: CheckableShipment) => {
            return {
              ...shipment,
              billItems: shipment.billItems.map((item) => {
                return {
                  ...item,
                  checked: this.editMode
                    ? item.billId === this.billId
                    : !!item.billId
                };
              }),
              checked: false
            };
          }
        );
        processedData.forEach((processedShipment) => {
          for (let i = 0; i < processedShipment?.billItems?.length; i++) {
            this.updateSection(processedShipment, i);
          }
          this.updateShipment(processedShipment);
        });
        this.updateAll(processedData);
        return processedData;
      },
      fetchPolicy: 'no-cache'
    }
  },
  components: {
    'el-button': Button,
    'el-card': Card,
    'el-checkbox': Checkbox,
    'el-form': Form,
    'el-form-item': FormItem,
    'el-option': Option,
    'el-popconfirm': Popconfirm,
    'el-select': Select
  }
})
export default class SelectBillItems extends Vue {
  @Prop({ required: true, type: Array }) shipmentIds!: string[];
  @Prop({ default: null, type: String }) billId!: string | null;
  @Prop({ default: null, type: String })
  billLanguage!: AvailableLocalesEnum | null;

  private isLoading = false;
  private language: AvailableLocalesEnum =
    this.billLanguage || AvailableLocalesEnum.ja;

  private readonly languageOptions = languageOptions();
  private shipments: CheckableShipment[] = [];
  private editMode = !!this.billId;
  private allShipments: CheckboxStatus = {
    checked: false,
    indeterminate: false
  };

  /**
   * Update checkboxes status
   */

  /**
   * Updates the global checkbox status as a result of the children statuses
   * @param shipmentList shipmentList related global checkbox to be updated
   */
  private updateAll(shipmentList) {
    // Bottom Top
    this.allShipments = this.checkChildren(shipmentList);
  }

  /**
   * Updates the parent shipment status as a result of the children statuses
   * @param shipment shipment to be updated
   * @param bubble   whether the update should bubble (defaults to false)
   */
  private updateShipment(shipment, bubble = false) {
    // Bottom Top
    const filteredBillItems = this.filterActiveBillItems(shipment?.billItems);

    shipment = Object.assign(shipment, this.checkChildren(filteredBillItems));

    // Bubble
    if (bubble) {
      this.updateAll(this.shipments);
    }
  }

  /**
   * Updates the parent section status as a result of the children statuses
   * @param shipment shipment containing the indexed item
   * @param index    index of the item inside the shipment's billItem list
   * @param bubble   whether the update should bubble (defaults to false)
   */
  private updateSection(
    shipment: CheckableShipment,
    index: number,
    bubble = false
  ) {
    const sectionStartIndex = this.findParentSectionIndex(shipment, index);
    let sectionHeaderItem = shipment?.billItems?.[sectionStartIndex];
    if (sectionHeaderItem) {
      const sectionItems: CheckableBillItem[] = this.sectionSelector(
        shipment,
        sectionStartIndex
      );
      const filteredBillItems = this.filterActiveBillItems(sectionItems);

      sectionHeaderItem = Object.assign(
        sectionHeaderItem,
        this.checkChildren(filteredBillItems)
      );
    }

    // Bubble
    if (bubble) {
      this.updateShipment(shipment, true);
    }
  }

  /**
   * Handle changes on the checkbox
   */

  private handleCheckAllChange(val) {
    // Top Bottom
    this.shipments.forEach((s) => {
      s.checked = val;
      // Change every shipment's value
      this.handleShipmentChange(s, val);
    });
    this.allShipments.indeterminate = false;
  }

  // select all item corresponding to the shipment
  private handleShipmentChange(shipment: CheckableShipment, value): void {
    // Top Bottom
    shipment.billItems &&
      shipment.billItems.forEach((item) => {
        item.checked = value;
        item.indeterminate = false;
      });
    shipment.indeterminate = false;

    // Bottom Top
    this.updateAll(this.shipments);
  }

  // select all items corresponding to the section
  private handleSectionChange(
    sectionItem: CheckableBillItem,
    shipment: CheckableShipment,
    start: number,
    value
  ) {
    // Top Bottom
    const billItems: CheckableBillItem[] = [sectionItem].concat(
      this.sectionSelector(shipment, start)
    );

    billItems.forEach((item: CheckableBillItem) => {
      if (item.billId === this.billId || !item.billId) {
        item.checked = value;
      }
    });
    sectionItem.indeterminate = false;

    // Bottom Top
    this.updateShipment(shipment, true);
  }

  private handleItemChange(shipment, index) {
    // Bottom Top
    this.updateSection(shipment, index, true);
  }

  /**
   * Utilities
   */

  /**
   * Checkes the individual statuses of the children
   * @param childrenList list whose parent status is to be determined
   * @returns            checkbox status for the parent
   */
  private checkChildren(childrenList): CheckboxStatus {
    const status = {
      checked: false,
      indeterminate: false
    };
    const totalCount = childrenList.length;

    let checkedCount = 0;
    let indeterminateCount = 0;
    childrenList.forEach((child) => {
      if (child.checked) {
        checkedCount++;
      }
      if (child.indeterminate) {
        indeterminateCount++;
      }
    });
    if (checkedCount === 0) {
      // none checked
      status.checked = false;
      status.indeterminate = indeterminateCount > 0;
    } else if (checkedCount < totalCount) {
      // indeterminate
      status.checked = false;
      status.indeterminate = true;
    } else {
      // all checked
      status.checked = true;
      status.indeterminate = false;
    }

    return status;
  }

  /**
   * Finds the index of the section item representing the section containing the item
   * @param shipment shipment containing the indexed item
   * @param index    index of the item inside the shipment's billItem list
   * @returns        index of the section item
   */
  private findParentSectionIndex(
    shipment: CheckableShipment,
    index: number
  ): number {
    // Test if current item is a section
    let currentItem = shipment?.billItems?.[index];
    if (currentItem.isSection) {
      return index;
    }
    for (let i = index - 1; i >= 0; i--) {
      currentItem = shipment?.billItems?.[i];
      if (currentItem.isSection) {
        return i;
      }
    }
  }

  /**
   * Filters a BillItem list by active BillItems without sections
   * @param billItemList list to be filtered
   * @returns            filtered BillItem list
   */
  private filterActiveBillItems(
    billItemList: CheckableBillItem[]
  ): CheckableBillItem[] {
    const filteredItems = [];
    billItemList.forEach((item) => {
      if (
        (item.billId === this.billId || item.billId === null) &&
        !item.isSection
      ) {
        filteredItems.push(item);
      }
    });
    return filteredItems;
  }

  private sectionSelector(
    shipment: CheckableShipment,
    start: number
  ): CheckableBillItem[] {
    const subSection: CheckableBillItem[] = [];
    const length: number =
      (shipment.billItems && shipment.billItems.length) || 0;

    for (let index = start + 1; index < length; index += 1) {
      if (shipment.billItems && shipment.billItems[index].isSection) {
        break;
      }
      subSection.push(shipment.billItems![index]);
    }

    return subSection;
  }

  private subSectionBilledCheck(
    shipment: CheckableShipment,
    start: number
  ): boolean {
    const subSection: CheckableBillItem[] = this.sectionSelector(
      shipment,
      start
    );

    if (this.editMode) {
      return subSection.some((item: CheckableBillItem) => {
        return item.billId === null || item.billId === this.billId;
      });
    }

    return subSection.some((item: CheckableBillItem) => !item.billId);
  }

  get canSubmit(): boolean {
    // this hunts down for any checked: true billItems
    return !(
      this.allShipments.checked === false &&
      this.allShipments.indeterminate === false
    );
  }

  private submit(): void {
    this.editMode ? this.updateBill() : this.createBill();
  }

  private get isPrivateSelected(): boolean {
    return this.shipments.some((shipment) => {
      return shipment.billItems.some((item) => {
        /**
         * only billed items that are
         * - not section nor already billed
         * - selected and public
         */
        return (
          !item.isSection &&
          !item.billId && item.checked === true &&
          item.isPrivate === true
        );
      });
    });
  }

  /**
   * Graphql mutations
   */

  private async createBill(): Promise<void> {
    const billItemIds: string[] = [];

    // create an array of ids to be sent to server
    this.shipments.forEach((shipment) => {
      shipment.billItems.forEach((item) => {
        // only billed items that have not been previously billed
        if (!item.billId && item.checked === true) {
          billItemIds.push(item.id);
        }
      });
    });

    if (!billItemIds.length) {
      return;
    }

    try {
      this.isLoading = true;
      const response = await billService.createPartialBill(
        billItemIds,
        this.language
      );

      if (response && response.data) {
        this.$router.push({
          name: 'billDetails',
          params: { id: (response.data as any).createBill.id }
        });
      }
    } catch (error) {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    } finally {
      this.isLoading = false;
    }
  }

  private async updateBill(): Promise<void> {
    const billItemIds: string[] = [];

    this.shipments.forEach((shipment) => {
      shipment.billItems.forEach((item) => {
        if (item.checked === true) {
          billItemIds.push(item.id);
        }
      });
    });

    if (!billItemIds.length) {
      return;
    }

    try {
      this.isLoading = true;
      const response = await billService.updateBill(
        { id: this.billId, language: this.language },
        billItemIds
      );

      if (response && response.data) {
        this.$router.push({
          name: 'billDetails',
          params: { id: response.data.updateBill.id }
        });
      }
    } catch (error) {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    } finally {
      this.isLoading = false;
    }
  }
}
